'use client';

/**
 * Surfaced on a freshly-launched board's page when the launch wizard's
 * inline metadata POST didn't land before the user navigated away from
 * the wizard. The wizard always stashes the payload to `localStorage`
 * via `setPendingMetadata` BEFORE attempting the POST, and the lobby's
 * `usePendingMetadataFlush` hook re-tries with the current Privy
 * access token on every board mount + wallet reconnect.
 *
 * That retry is silent by default (see the comment block at the top of
 * `use-pending-metadata-flush.ts` — we don't want to nag with toasts on
 * the lobby page). But on the freshly-launched board itself, "silent"
 * isn't the right call: a creator who closes the tab during the retry
 * window loses their picks for good once the entry ages out at 24h.
 *
 * The banner appears for the active board only, only while the targeted
 * compositeGridId still has a pending entry, and self-conceals the
 * moment the flusher confirms the POST landed (or the entry ages out
 * past `MAX_AGE_MS`). Visual language follows `BoardInitializingBanner`
 * — compact, amber-toned, full-bleed inside its column — so it sits
 * naturally next to the other board-state banners.
 */

import { Loader2, Save } from 'lucide-react';

import { cn } from '@/lib/utils';

interface PendingMetadataBannerProps {
  /** True while there's a pending metadata write in `localStorage` for
   *  this board. From `usePendingMetadataFlush({ onlyForCompositeGridId })`'s
   *  return value — the hook re-snapshots the entry after every flush
   *  attempt, so this drops to `false` automatically once the POST
   *  succeeds (or the entry ages out via the 24h floor). */
  isPending: boolean;
  /** True while a flush attempt is in-flight. Used to swap the copy
   *  from "queued" to "saving" so the user can tell something is
   *  happening; both states ask them to keep the tab open. */
  isFlushing: boolean;
  className?: string;
}

export function PendingMetadataBanner({
  isPending,
  isFlushing,
  className,
}: PendingMetadataBannerProps) {
  if (!isPending) return null;

  return (
    <div
      className={cn(
        'rounded-[6px] border border-amber-400/40 bg-amber-500/10 backdrop-blur-md p-3 text-[11px] text-amber-50 space-y-1.5',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-2">
        {isFlushing ? (
          <Loader2 className="h-4 w-4 mt-0.5 shrink-0 text-amber-200 animate-spin" />
        ) : (
          <Save className="h-4 w-4 mt-0.5 shrink-0 text-amber-200" />
        )}
        <div className="space-y-1">
          <p className="font-mono uppercase tracking-[0.2em] text-amber-200 text-[9px]">
            {isFlushing ? 'Saving board metadata' : 'Board metadata queued'}
          </p>
          <p className="text-amber-50/90">
            {isFlushing
              ? 'Sending your board picks to the server. Keep this tab open until it finishes.'
              : 'Your board picks haven\u2019t reached the server yet \u2014 keep this tab open and we\u2019ll keep retrying.'}
          </p>
        </div>
      </div>
    </div>
  );
}
